/**
 * @description A context that provides an API for displaying toast messages. The API provides the following methods (details below):
 * success, error, and info.
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import * as ReactDOM from 'react-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import FadeInFadeOut from '@seccon/shared/ui/components/FadeInFadeOut';

export const ToastContext = React.createContext({
  error: () => console.error('Toast Provider is not defined'),
  info: () => console.error('Toast Provider is not defined'),
  success: () => console.error('Toast Provider is not defined'),
  warning: () => console.error('Toast Provider is not defined')
});

const styles = {
  toast: {
    zIndex: 'snackbar'
  }
};

/**
 * @prop children {React.Element | React.Element[]} - react elements to wrap the provider in
 * @prop toastRef {React.RefObject<*>} - reference to the toast container element
 */
export const ToastProvider = ({ children, toastRef }) => {
  // Queue of toast banners being displayed
  const [queue, setQueue] = React.useState({});

  /* A reference that needs track of the new toast banner queue to be displayed when new banners are added/removed
   *
   * Note that we're updating the queue state using a reference instead of just updating the state directly because we want to
   * support the use case where banners could be shown multiple times in the same render cycle to show multiple toast messages
   * at once. Without this, the most recent show function call would override the new state of all its previous calls in the
   * same render cycle.  With it, this reference will keep track of the new state with show function call and updates react
   * state based on that new state.
   */
  const newQueue = React.useRef({});

  // Keeps track of the most recent toast id
  const toastId = React.useRef(0);

  /**
   * A private function that is called when the message is displayed. It calls the toast's promise resolve callback
   * @param id {number} - numeric id of the toast message being displayed
   */
  function handleShow (id) {
    newQueue.current[id].showResolve();
  }

  /**
   * A private function that is called when the message has finished disppearing. It removes the toast element from the queue
   * @param id {number} - numeric id of the toast message that just disappeared
   */
  function handleFinish (id) {
    delete newQueue.current[id];

    setQueue(newQueue.current);
  }

  /**
   * A private function that handles the close button click callback by removing the toast element from the queue
   */
  function handleClose (id) {
    delete newQueue.current[id];

    setQueue(newQueue.current);
  }

  /**
   * A private function used to add new toast messages to the queue
   * @param {object} - toast message settings
   * @returns {Promise} - a promise to be resolved once the message is displayed
   */
  function show (toast) {
    return new Promise((resolve) => {
      // Add the message to the queue based on the generated id (increment after use) and add the promise resolver to the
      // configuration
      newQueue.current[toastId.current++] = {
        ...toast,
        showResolve: resolve
      };

      setQueue(newQueue.current);
    });
  }

  /**
   * Public API that adds an error toast message to the queue
   * @param message {string | React.Element} - error message to be displayed
   * @param settings {{ action: React.Element, title: string }} - additional configuration settings, such as action elements or title
   * @returns {Promise} - a promise to be resolved once the message is displayed
   */
  function error (message, settings = {}) {
    return show({ ...settings, message, severity: 'error' });
  }

  /**
   * Public API that adds an info toast message to the queue
   * @param message {string | React.Element} - info message to be displayed
   * @param settings {{ action: React.Element, title: string }} - additional configuration settings, such as action elements or title
   * @returns {Promise} - a promise to be resolved once the message is displayed
   */
  function info (message, settings = {}) {
    return show({ ...settings, message, severity: 'info' });
  }

  /**
   * Public API that adds a success toast message to the queue
   * @param message {string | React.Element} - success message to be displayed
   * @param settings {{ action: React.Element, title: string }} - additional configuration settings, such as action elements or title
   * @returns {Promise} - a promise to be resolved once the message is displayed
   */
  function success (message, settings = {}) {
    return show({ ...settings, message, severity: 'success' });
  }

  /**
   * Public API that adds a warning toast message to the queue
   * @param message {string | React.Element} - warning message to be displayed
   * @param settings {{ action: React.Element, title: string }} - additional configuration settings, such as action elements or title
   * @returns {Promise} - a promise to be resolved once the message is displayed
   */
  function warning (message, settings = {}) {
    return show({ ...settings, message, severity: 'warning' });
  }

  /**
   * Keep the newQueue reference in sync with the react state
   */
  React.useEffect(() => {
    newQueue.current = { ...queue };
  }, [queue]);

  /**
   * This reference defines the API. We use a reference so that way a new API object isn't defined every render cycle
   */
  const apiRef = React.useRef({
    error,
    info,
    success,
    warning
  });

  const toastList = (
    <Stack spacing={1}>
      {Object.keys(queue).map((id) => {
        const {
          action,
          message,
          severity,
          title
        } = queue[id];

        return (
          <FadeInFadeOut key={`toast-${ id }`} onShow={() => handleShow(id)} onFinish={() => handleFinish(id)} sx={styles.toast}>
            <Alert action={action} severity={severity} onClose={() => handleClose(id)}>
              {title ? <AlertTitle>{title}</AlertTitle> : null}
              {typeof message === 'string' ? <Typography>{message}</Typography> : message}
            </Alert>
          </FadeInFadeOut>
        );
      })}
    </Stack>
  );

  return (
    <ToastContext.Provider value={apiRef.current}>
      {children}
      {toastRef.current ? ReactDOM.createPortal(toastList, toastRef.current) : null}
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
  toastRef: PropTypes.shape({ current: PropTypes.object }).isRequired
};
